/**
 * Role-Based Access Control (RBAC)
 * SECURITY: Deny by default
 */

import { UserRole } from "@/state/session";

type Resource =
  | "dashboard"
  | "products"
  | "ingredients"
  | "stock"
  | "stock_entries"
  | "batches"
  | "sales"
  | "reports"
  | "users"
  | "settings"
  | "tipo_produtos"
  | "estabelecimentos"
  | "clientes"
  | "vendas"
  | "receitas"
  | "tabelas"
  | "billing";

type Action = "view" | "create" | "edit" | "delete" | "manage";

export type { Resource, Action };

// Permission matrix: deny by default
const permissions: Record<UserRole, Partial<Record<Resource, Action[]>>> = {
  admin: {
    dashboard: ["view"],
    products: ["view", "create", "edit", "delete"],
    ingredients: ["view", "create", "edit", "delete"],
    stock: ["view", "create", "edit"],
    stock_entries: ["view", "create", "edit", "delete"],
    batches: ["view", "create", "edit"],
    sales: ["view", "create"],
    reports: ["view"],
    users: ["view", "create", "edit", "delete", "manage"],
    settings: ["view", "edit", "manage"],
    tipo_produtos: ["view", "create", "edit", "delete"],
    estabelecimentos: ["view", "create", "edit", "delete"],
    clientes: ["view", "create", "edit", "delete"],
    vendas: ["view", "create", "edit", "delete"],
    receitas: ["view", "create", "edit", "delete"],
    tabelas: ["view", "manage"],
    billing: ["view", "create", "edit", "delete", "manage"],
  },
  gestor: {
    dashboard: ["view"],
    products: ["view", "create", "edit", "delete"],
    ingredients: ["view", "create", "edit"],
    stock: ["view", "edit"],
    stock_entries: ["view", "create", "edit", "delete"],
    batches: ["view", "create", "edit"],
    sales: ["view", "create"],
    reports: ["view"],
    tipo_produtos: ["view", "create", "edit"],
    estabelecimentos: ["view"],
    clientes: ["view", "create", "edit"],
    vendas: ["view", "create", "edit"],
    receitas: ["view", "create", "edit"],
  },
  funcionario: {
    dashboard: ["view"],
    products: ["view", "create", "edit"],
    ingredients: ["view", "create"],
    stock: ["view"],
    stock_entries: ["view", "create", "edit"],
    batches: ["create"],
    sales: ["create"],
    tipo_produtos: ["view"],
    estabelecimentos: ["view"],
    clientes: ["view", "create"],
    vendas: ["view", "create"],
    receitas: ["view", "create"],
  },
};

/**
 * Check if user role has permission for action on resource
 * Returns false by default (secure by default)
 */
export function can(role: UserRole | undefined, resource: Resource, action: Action): boolean {
  if (!role) return false;
  
  const rolePermissions = permissions[role];
  if (!rolePermissions) return false;

  const resourceActions = rolePermissions[resource];
  if (!resourceActions) return false;

  return resourceActions.includes(action);
}

/**
 * Check if role can access a route
 */
export function canAccessRoute(role: UserRole, route: string): boolean {
  // Public routes
  const publicRoutes = ["/login", "/reset-password"];
  if (publicRoutes.some((r) => route.startsWith(r))) return true;

  // Profile is accessible by all authenticated users
  if (route.startsWith("/profile")) return true;

  const routeMap: Record<string, { resource: Resource; action: Action }> = {
    "/dashboard": { resource: "dashboard", action: "view" },
    "/cadastro": { resource: "products", action: "view" },
    "/entrada": { resource: "ingredients", action: "view" },
    "/estoque": { resource: "stock", action: "view" },
    "/fornada": { resource: "batches", action: "view" },
    "/vendas": { resource: "vendas", action: "create" },
    "/relatorios": { resource: "reports", action: "view" },
    "/admin": { resource: "users", action: "manage" },
    "/users": { resource: "users", action: "manage" },
    "/tabelas": { resource: "tabelas", action: "manage" },
    "/settings": { resource: "settings", action: "view" },
    "/produtos": { resource: "tipo_produtos", action: "view" },
    "/estabelecimentos": { resource: "estabelecimentos", action: "view" },
    "/clientes": { resource: "clientes", action: "view" },
    "/receitas": { resource: "receitas", action: "view" },
  };

  const match = Object.entries(routeMap).find(([path]) => route.startsWith(path));
  if (!match) return false; // Deny unknown routes by default

  const [, { resource, action }] = match;
  return can(role, resource, action);
}
